import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import type { DocumentReferencesView } from 'api';
import { DropProps } from '../CurrentRequestedDocumentsAndQuestions/types';

// Styled component for drag-over styling
export const DragOverContainer = styled('div')(({ theme }) => ({
  '&.drag-over': {
    outline: `1.5px dashed ${theme.palette.primary.main}`,
  },
}));

// CSS class name for drag over state
const DRAG_OVER_CLASS = 'drag-over';

export const useDragRow = (
  setDraggedRow: (row: DocumentReferencesView | null) => void,
  setDraggedSelectedFiles?: (files: DocumentReferencesView[] | null) => void,
  selectedFiles?: DocumentReferencesView[]
) => {
  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLTableRowElement>, row: DocumentReferencesView) => {
      // Check if this row is part of a multi-selection
      const isMultiSelect = selectedFiles && selectedFiles.length > 1 && selectedFiles.some(f => f.file.id === row.file.id);
      
      if (isMultiSelect && setDraggedSelectedFiles && selectedFiles) {
        // Validate all selected files belong to the same md_documents
        const firstMdDocuments = selectedFiles[0]?.md_documents;
        const allSameMdDocuments = selectedFiles.every(file => file.md_documents === firstMdDocuments);
        
        if (allSameMdDocuments && firstMdDocuments) {
          // Multi-file drag: set all selected files
          setDraggedSelectedFiles(selectedFiles);
          setDraggedRow(null);
          // eslint-disable-next-line no-param-reassign
          event.dataTransfer.effectAllowed = 'move';
        } else {
          // Files from different measurement dates - prevent drag
          // Setting effectAllowed to 'none' prevents the drag operation
          // eslint-disable-next-line no-param-reassign
          event.dataTransfer.effectAllowed = 'none';
        }
      } else {
        // Single file drag: use existing behavior
        setDraggedRow(row);
        if (setDraggedSelectedFiles) {
          setDraggedSelectedFiles(null);
        }
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.effectAllowed = 'move';
      }
    },
    [setDraggedRow, setDraggedSelectedFiles, selectedFiles]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedRow(null);
    if (setDraggedSelectedFiles) {
      setDraggedSelectedFiles(null);
    }
  }, [setDraggedRow, setDraggedSelectedFiles]);

  return { handleDragStart, handleDragEnd };
};

export const useDropRow = (props: DropProps & { onDropSuccess?: (draggedFiles: DocumentReferencesView[]) => void }) => {
  const { draggedRow, draggedSelectedFiles, addFilesToFolder, refreshDocuments, onDropSuccess } = props;

  const handleDragOver = (event: React.DragEvent<HTMLTableRowElement | HTMLTableSectionElement>) => {
    event.preventDefault();
    event.currentTarget.classList.add(DRAG_OVER_CLASS);
  };

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLTableRowElement | HTMLTableSectionElement>) => {
      event.currentTarget.classList.remove(DRAG_OVER_CLASS);
    },
    []
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLTableRowElement | HTMLTableSectionElement>, folderId?: number) => {
      event.preventDefault();
      
      // Check if we're dropping multiple selected files
      if (draggedSelectedFiles && draggedSelectedFiles.length > 0) {
        // Validate all files belong to the same md_documents
        const firstMdDocuments = draggedSelectedFiles[0]?.md_documents;
        const allSameMdDocuments = draggedSelectedFiles.every(file => file.md_documents === firstMdDocuments);
        
        if (allSameMdDocuments && firstMdDocuments) {
          // Extract all document_reference_ids
          const documentReferenceIds = draggedSelectedFiles
            .map(file => file.document_reference_id)
            .filter((id): id is number => id !== undefined && id !== null);
          
          if (documentReferenceIds.length > 0) {
            const data = {
              folder_id: folderId,
              document_references_ids: documentReferenceIds,
            };
            handleDragLeave(event);
            await addFilesToFolder(firstMdDocuments, data, true);
            refreshDocuments();
            if (onDropSuccess) {
              // Only pass files that were actually moved (those with valid document_reference_ids)
              const successfullyMovedFiles = draggedSelectedFiles.filter(
                file => file.document_reference_id !== undefined && file.document_reference_id !== null
              );
              onDropSuccess(successfullyMovedFiles);
            }
          }
        }
      } else if (draggedRow?.document_reference_id) {
        // Single file drag: use existing behavior
        const data = {
          folder_id: folderId,
          document_references_ids: [draggedRow.document_reference_id],
        };
        handleDragLeave(event);
        await addFilesToFolder(draggedRow.md_documents, data, true);
        refreshDocuments();
        if (onDropSuccess) {
          onDropSuccess([draggedRow]);
        }
      }
    },
    [draggedRow, draggedSelectedFiles, addFilesToFolder, refreshDocuments, handleDragLeave, onDropSuccess]
  );

  return { handleDragOver, handleDragLeave, handleDrop };
};
